import { useEffect, useRef } from 'react'
import type P5 from 'p5'
import type * as MatterNS from 'matter-js'
import { canHover, prefersReducedMotion } from '@/lib/hooks'

/**
 * FallingLogos
 *
 * A full-screen, physics-based animation: rune-logo tiles drop from above and
 * pile up using a Matter.js rigid-body simulation, rendered with p5.js.
 *
 * p5.js, Matter.js and poly-decomp are imported dynamically inside the effect so
 * they only load on routes that mount this component (e.g. /contact), keeping
 * them out of the main bundle.
 *
 * Interaction (mouse-dragging tiles) is only enabled on devices with a fine
 * pointer + hover, so touch users keep normal scrolling/tapping on the page.
 */

// --- Config -----------------------------------------------------------------

const LOGO_SCALE = 0.44
const LOGO_MARGIN = 50
const GRAVITY_Y = 1.0
const MOUSE_STIFFNESS = 0.2
const LOGO_COUNT_MIN = 32
const LOGO_COUNT_MAX = 60
const REFERENCE_AREA = 1280 * 800 // baseline viewport for the count formula
const SAMPLES_PER_PATH = 2000
const SVG_WIDTH = 184
const SVG_HEIGHT = 397
const SHOW_OUTLINE = false // debug: draw the physics hull

const PHYSICS_OPTIONS: MatterNS.IChamferableBodyDefinition = {
  restitution: 0.3,
  friction: 0.5,
  frictionAir: 0.01,
  density: 0.001,
}

// The rune mark — 6 layered paths in two greens. Used both as the rendered
// tile (rasterised via loadImage) and as the source for the physics hull.
const LOGO_PATHS: ReadonlyArray<{ d: string; fill: string }> = [
  { d: 'M56.0868 206.773C43.4753 219.398 36.3906 236.516 36.3906 254.363V397L36.3867 396.997L36.3886 397H0L18.1797 384.379V246.493C18.1797 228.877 25.175 211.978 37.624 199.521L145.808 91.146L184.002 78.7681L56.0868 206.773Z', fill: '#A6F3C5' },
  { d: 'M94.0159 49.4109L37.6227 106.013C25.1737 118.47 18.1785 135.368 18.1785 152.985V243.805C19.5349 208.347 29.4392 191.41 36.3951 174.943V157.702C36.3951 145.088 42.4711 126.793 56.5008 112.749L132.221 37.0327L94.0159 49.4165V49.4109Z', fill: '#A6F3C5' },
  { d: 'M36.391 66.9162C36.391 47.1627 44.3839 31.6718 57.158 18.8954L76.1819 0L56.3677 7.57161C33.6893 16.2874 18.7797 34.8182 18.1967 58.3631C18.1855 58.9128 18.1743 60.9375 18.1743 61.4927V150.311C19.5308 114.853 29.435 97.915 36.391 81.4481V66.9219V66.9162Z', fill: '#A6F3C5' },
  { d: 'M184.002 78.7681L145.809 91.146H145.804L37.6186 199.521C25.1697 211.977 18.1743 228.877 18.1743 246.493V243.807C19.5364 208.349 29.4351 191.411 36.3911 174.944L132.481 78.8354L132.547 78.7681H184.002Z', fill: '#67CF91' },
  { d: 'M56.456 7.53807L76.1805 0H0V396.999L18.1777 384.385V61.4364L18.1953 58.3124C18.7783 34.7677 34.1365 15.9846 56.456 7.53807Z', fill: '#67CF91' },
  { d: 'M132.218 37.0327L94.0133 49.4106L94.0153 49.4126L37.6228 106.014C25.1738 118.471 18.1785 135.37 18.1785 152.987V150.255C19.5405 114.798 29.4393 97.8706 36.3952 81.4037L80.6911 37.1011L80.6901 37.1001L80.7575 37.0327H132.218Z', fill: '#67CF91' },
]

const SVG_STRING =
  `<svg width="${SVG_WIDTH}" height="${SVG_HEIGHT}" viewBox="0 0 ${SVG_WIDTH} ${SVG_HEIGHT}" fill="none" xmlns="http://www.w3.org/2000/svg">` +
  LOGO_PATHS.map((p) => `<path d="${p.d}" fill="${p.fill}"/>`).join('') +
  `</svg>`

const SVG_NS = 'http://www.w3.org/2000/svg'

// --- Geometry helpers -------------------------------------------------------

/**
 * Sample points along every logo path, take the overall convex hull, then
 * centre it on the SVG bounding-box centre and scale by LOGO_SCALE. Returns the
 * hull vertices ready for Bodies.fromVertices (in canvas pixels).
 */
function buildHullVertices(Matter: typeof MatterNS): MatterNS.Vector[] {
  const svg = document.createElementNS(SVG_NS, 'svg')
  svg.setAttribute('width', String(SVG_WIDTH))
  svg.setAttribute('height', String(SVG_HEIGHT))
  svg.style.position = 'absolute'
  svg.style.left = '-9999px'
  svg.style.top = '0'
  svg.style.visibility = 'hidden'
  document.body.appendChild(svg)

  const points: MatterNS.Vector[] = []
  try {
    for (const { d } of LOGO_PATHS) {
      const path = document.createElementNS(SVG_NS, 'path')
      path.setAttribute('d', d)
      svg.appendChild(path)
      const len = path.getTotalLength()
      if (!len) continue
      for (let i = 0; i <= SAMPLES_PER_PATH; i++) {
        const pt = path.getPointAtLength((i / SAMPLES_PER_PATH) * len)
        points.push({ x: pt.x, y: pt.y })
      }
    }
  } finally {
    document.body.removeChild(svg)
  }

  // hull() only reads x/y; Vector is structurally sufficient here.
  const hull = Matter.Vertices.hull(points as unknown as MatterNS.Vertex[])
  return hull.map((v) => ({
    x: (v.x - SVG_WIDTH / 2) * LOGO_SCALE,
    y: (v.y - SVG_HEIGHT / 2) * LOGO_SCALE,
  }))
}

// --- Component --------------------------------------------------------------

export default function FallingLogos() {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (prefersReducedMotion) return
    const container = containerRef.current
    if (!container) return

    let cancelled = false
    let cleanup: (() => void) | undefined

    void (async () => {
      const [{ default: P5Ctor }, Matter, { default: decomp }] = await Promise.all([
        import('p5'),
        import('matter-js'),
        import('poly-decomp'),
      ])
      if (cancelled || !containerRef.current) return

      // Matter needs poly-decomp for concave decomposition.
      Matter.Common.setDecomp(decomp)

      const imgW = SVG_WIDTH * LOGO_SCALE
      const imgH = SVG_HEIGHT * LOGO_SCALE

      let logoImage: P5.Image
      let engine: MatterNS.Engine
      let mouseConstraint: MatterNS.MouseConstraint | null = null
      let walls: MatterNS.Body[] = []
      let logos: MatterNS.Body[] = []
      let hullVerts: MatterNS.Vector[] = []
      // Offset (in body-local space) from the body centroid to the SVG bbox
      // centre, so the rendered image lines up with the physics shape.
      let drawOffsetX = 0
      let drawOffsetY = 0

      let active = false
      let hasDropped = false

      const rand = (min: number, max: number) => min + Math.random() * (max - min)

      const makeWalls = (w: number, h: number): MatterNS.Body[] => [
        Matter.Bodies.rectangle(w / 2, h + 25, w + 100, 50, { isStatic: true }),
        Matter.Bodies.rectangle(w / 2, -25, w + 100, 50, { isStatic: true }),
        Matter.Bodies.rectangle(-25, h / 2, 50, h + 100, { isStatic: true }),
        Matter.Bodies.rectangle(w + 25, h / 2, 50, h + 100, { isStatic: true }),
      ]

      const logoCount = (w: number, h: number): number => {
        let count = Math.round(5.4 * ((w * h) / REFERENCE_AREA))
        count = Math.max(LOGO_COUNT_MIN, count)
        return Math.min(LOGO_COUNT_MAX, count)
      }

      const spawnLogos = (w: number, h: number): MatterNS.Body[] => {
        const bodies: MatterNS.Body[] = []
        const n = logoCount(w, h)
        for (let i = 0; i < n; i++) {
          const x = rand(LOGO_MARGIN, w - LOGO_MARGIN)
          const y = rand(LOGO_MARGIN, h - LOGO_MARGIN)
          const body = Matter.Bodies.fromVertices(x, y, [hullVerts], PHYSICS_OPTIONS, true)
          if (body) bodies.push(body)
        }
        return bodies
      }

      const freezeAll = () => {
        for (const body of logos) {
          Matter.Body.setVelocity(body, { x: 0, y: 0 })
          Matter.Body.setAngularVelocity(body, 0)
        }
      }

      const dropAll = () => {
        for (const body of logos) {
          body.isSleeping = false
          Matter.Body.setVelocity(body, { x: rand(-2, 2), y: rand(3, 6) })
          Matter.Body.setAngularVelocity(body, rand(-0.1, 0.1))
        }
      }

      const sketch = (p: P5) => {
        p.preload = () => {
          logoImage = p.loadImage(
            'data:image/svg+xml;base64,' + btoa(SVG_STRING),
          )
        }

        p.setup = () => {
          const w = window.innerWidth
          const h = window.innerHeight
          const renderer = p.createCanvas(w, h)
          const canvasEl = renderer.elt as HTMLCanvasElement
          canvasEl.style.display = 'block'
          // Map mouse coords 1:1 to canvas/physics coords.
          p.pixelDensity(1)

          engine = Matter.Engine.create({ enableSleeping: true })
          engine.gravity.y = GRAVITY_Y

          hullVerts = buildHullVertices(Matter)
          const centroid = Matter.Vertices.centre(hullVerts)
          drawOffsetX = -centroid.x
          drawOffsetY = -centroid.y

          walls = makeWalls(w, h)
          logos = spawnLogos(w, h)
          Matter.World.add(engine.world, [...walls, ...logos])

          if (canHover) {
            const mouse = Matter.Mouse.create(canvasEl)
            // Let the page keep handling wheel/scroll over the canvas. The wheel
            // handler isn't in the public types, so reach it structurally.
            const mouseWheel = (mouse as unknown as { mousewheel: EventListener }).mousewheel
            canvasEl.removeEventListener('wheel', mouseWheel)
            canvasEl.removeEventListener('DOMMouseScroll', mouseWheel)
            mouseConstraint = Matter.MouseConstraint.create(engine, {
              mouse,
              constraint: { stiffness: MOUSE_STIFFNESS, render: { visible: false } },
            })
            Matter.World.add(engine.world, mouseConstraint)
          }

          freezeAll()
        }

        p.draw = () => {
          p.clear()
          if (active) Matter.Engine.update(engine, 1000 / 60)

          p.imageMode(p.CORNER)
          for (const body of logos) {
            p.push()
            p.translate(body.position.x, body.position.y)
            p.rotate(body.angle)
            p.image(
              logoImage,
              drawOffsetX - imgW / 2,
              drawOffsetY - imgH / 2,
              imgW,
              imgH,
            )
            if (SHOW_OUTLINE) {
              p.push()
              p.translate(-body.position.x, -body.position.y)
              p.noFill()
              p.stroke(255, 255, 0)
              p.beginShape()
              for (const v of body.vertices) p.vertex(v.x, v.y)
              p.endShape(p.CLOSE)
              p.pop()
            }
            p.pop()
          }
        }

        p.windowResized = () => {
          const w = window.innerWidth
          const h = window.innerHeight
          p.resizeCanvas(w, h)
          Matter.World.remove(engine.world, [...walls, ...logos])
          walls = makeWalls(w, h)
          logos = spawnLogos(w, h)
          Matter.World.add(engine.world, [...walls, ...logos])
          if (active) dropAll()
          else freezeAll()
        }
      }

      const p5Instance = new P5Ctor(sketch, container)

      // Lazily start the simulation once the canvas enters the viewport;
      // freeze it again when it leaves.
      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            active = true
            if (!hasDropped) {
              hasDropped = true
              dropAll()
            }
          } else {
            active = false
            freezeAll()
          }
        }
      })
      observer.observe(container)

      cleanup = () => {
        observer.disconnect()
        if (mouseConstraint) Matter.World.remove(engine.world, mouseConstraint)
        if (engine) {
          Matter.World.clear(engine.world, false)
          Matter.Engine.clear(engine)
        }
        p5Instance.remove()
      }
    })()

    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [])

  if (prefersReducedMotion) return null

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: canHover ? 'auto' : 'none',
        touchAction: 'pan-y',
      }}
    />
  )
}
